import { Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, ShoppingBag, Package, BarChart3, LogOut, Leaf, FileEdit, FolderTree, Truck, Box, GitBranch } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/admin/products', icon: Box, label: 'Products' },
  { to: '/admin/farmers', icon: Users, label: 'Farmers' },
  { to: '/admin/orders', icon: ShoppingBag, label: 'Orders' },
  { to: '/admin/distribution', icon: GitBranch, label: 'Order Distribution' },
  { to: '/admin/inventory', icon: Package, label: 'Inventory' },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { type: 'divider', label: 'Content' },
  { to: '/admin/content', icon: FileEdit, label: 'Pages & Homepage' },
  { to: '/admin/categories', icon: FolderTree, label: 'Categories' },
  { to: '/admin/delivery', icon: Truck, label: 'Delivery Settings' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  return (
    <div data-testid="admin-layout" className="flex min-h-screen bg-muted/30">
      <aside className="w-64 bg-card border-r border-border flex flex-col shrink-0">
        <div className="p-6 border-b border-border">
          <Link to="/admin" className="flex items-center gap-2">
            <Leaf className="h-5 w-5 text-primary" />
            <span className="font-serif font-semibold text-lg">Orgaanix Admin</span>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item, i) => item.type === 'divider' ? (
            <div key={i} className="pt-4 pb-1 px-3"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{item.label}</p></div>
          ) : (
            <Link key={item.to} to={item.to} data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z]/g, '')}`}
              className={cn('flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                isActive(item) ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}>
              <item.icon className="h-4 w-4" />{item.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3 px-3">
            <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center shrink-0">
              <span className="text-xs font-semibold text-primary-foreground">{user?.name?.[0]?.toUpperCase() || 'A'}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted mb-1">
            <Leaf className="h-4 w-4" /> View Store
          </Link>
          <button data-testid="admin-logout-button" onClick={logout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-destructive transition-colors w-full rounded-lg hover:bg-muted">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto"><Outlet /></main>
    </div>
  );
}
